"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Sinaliza na ABA que algo aconteceu enquanto o usuario estava fora.
 *
 * Dois canais complementares, como no fork
 * (`app/javascript/dashboard/helper/AudioAlerts/faviconHelper.js`):
 *  - `document.title` vira "(N) GK Platform" — o numero sobrevive a aba estreita
 *  - favicon ganha marcador — sobrevive a aba tao estreita que so cabe o icone
 *
 * Ambos limpam quando a aba volta ao foco (`visibilitychange`), igual ao
 * `initFaviconSwitcher` do fork.
 *
 * O contador soma DUAS fontes, com semanticas diferentes de proposito:
 *
 *  - EFEMERO (`notify`): evento que chegou com a aba sem foco. Zera quando o
 *    foco volta — "aconteceu enquanto voce estava fora" ja cumpriu seu papel.
 *  - PERSISTIDO (`setPersistentCount`): nao-lidas do sino e do Chat Interno
 *    (#466), uma fonte por origem, somadas. Aparece mesmo com a aba visivel e so
 *    cai quando o usuario efetivamente le, porque a pendencia existe
 *    independentemente de ele estar olhando a tela.
 */
export type FontePersistida = "sino" | "chatInterno";

interface TabBadgeValue {
  /** Registra um evento EFEMERO. No-op se a aba estiver visivel. */
  notify: () => void;
  /**
   * Total de pendencias PERSISTIDAS (nao-lidas do sino). Diferente do contador
   * efemero: aparece mesmo com a aba visivel, porque a pendencia existe
   * independentemente de o usuario estar olhando a plataforma.
   */
  setPersistentCount: (n: number, fonte?: FontePersistida) => void;
  /** Soma das duas fontes. */
  count: number;
}

const TabBadgeContext = createContext<TabBadgeValue>({
  notify: () => {},
  setPersistentCount: () => {},
  count: 0,
});

export function useTabBadge(): TabBadgeValue {
  return useContext(TabBadgeContext);
}

const BASE_TITLE = "GK Platform";
const ICON_BADGE = "/icon-badge.png";

/**
 * Troca TODOS os `link[rel~="icon"]`, nao um so — o App Router injeta o proprio
 * link a partir de `app/icon.png`, e quando ha varios os browsers resolvem pelo
 * ULTIMO declarado. Mexer num unico link deixava o icone da aba inalterado.
 * Mesma abordagem do fork (`faviconHelper.js` itera `.favicon`).
 *
 * O href original de cada link e guardado em `data-original-href` na primeira
 * troca, porque o do Next carrega hash de build (`/icon.png?<hash>`)
 * e nao pode ser reconstruido por constante.
 *
 * Os dois estados sao deliberadamente distintos em MASSA DE COR, nao em desenho:
 *
 *   normal  (`app/icon.png`)      simbolo da marca em #2E86C1, SEM fundo
 *   alerta  (`/icon-badge.png`)   simbolo branco sobre fundo azul cheio + ponto
 *                                 vermelho no canto
 *
 * A aba desenha o icone em 16px, e o simbolo ocupa so ~8% do quadro em tracos
 * finos — nesse tamanho o olho le a mancha de cor, nao o desenho. Por isso a
 * distincao e traco fino vs bloco cheio: sobrevive a reducao, enquanto qualquer
 * diferenca de detalhe se dissolveria. O ponto vermelho e grande pelo mesmo
 * motivo (um ponto discreto com anel fica elegante em 64px e some a 16px), no
 * espirito do `favicon-badge` do fork.
 *
 * Ambos usam o simbolo da marca; o logo horizontal completo virava borrao. O
 * normal ficou sem fundo a pedido do Kayque, pra reservar o icone azul cheio ao
 * estado de alerta — em #2E86C1 e nao no branco original, que sumia na aba de
 * tema claro.
 */
function setFaviconBadge(ativo: boolean): void {
  const links = document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]');

  links.forEach((link) => {
    if (ativo) {
      if (!link.dataset.originalHref) {
        link.dataset.originalHref = link.getAttribute("href") ?? "";
      }
      link.setAttribute("href", ICON_BADGE);
      return;
    }

    const original = link.dataset.originalHref;
    if (original) link.setAttribute("href", original);
  });
}

/**
 * Reescreve o titulo a partir da base, nunca do valor atual — evita empilhar
 * prefixo ("(1) (2) GK Platform") e corrige o caso em que a navegacao do Next
 * (metadata do App Router) sobrescreveu o titulo entre um evento e outro.
 */
function setTitleCount(n: number): void {
  document.title = n > 0 ? `(${n}) ${BASE_TITLE}` : BASE_TITLE;
}

export function TabBadgeProvider({ children }: { children: React.ReactNode }) {
  // Titulo que ESTE provider quer ver. Serve de referencia pro observer abaixo
  // distinguir "eu escrevi" de "alguem sobrescreveu".
  const tituloDesejadoRef = useRef(BASE_TITLE);
  // Duas fontes independentes:
  //  - efemero: eventos que chegaram com a aba sem foco; zera ao focar
  //  - persistido: nao-lidas do sino e do Chat Interno; so muda quando a fonte muda
  const [efemero, setEfemero] = useState(0);
  const [persistido, setPersistido] = useState(0);
  // Espelhos sincronos: duas chamadas no mesmo tick precisam enxergar o valor
  // ja atualizado pra escrever o titulo certo. Uma entrada por fonte: cada uma
  // reescreve SO o proprio total — o sino atualizando nao apaga o Chat Interno.
  const efemeroRef = useRef(0);
  const persistidoRef = useRef<Record<FontePersistida, number>>({
    sino: 0,
    chatInterno: 0,
  });

  const aplicar = useCallback(() => {
    const { sino, chatInterno } = persistidoRef.current;
    const total = efemeroRef.current + sino + chatInterno;
    tituloDesejadoRef.current = total > 0 ? `(${total}) ${BASE_TITLE}` : BASE_TITLE;
    setTitleCount(total);
    setFaviconBadge(total > 0);
  }, []);

  const notify = useCallback(() => {
    // Aba em foco: o usuario esta vendo a plataforma, o badge efemero seria
    // ruido. (O persistido continua valendo — a pendencia e real.)
    if (typeof document === "undefined") return;
    if (document.visibilityState === "visible") return;

    efemeroRef.current += 1;
    setEfemero(efemeroRef.current);
    aplicar();
  }, [aplicar]);

  const setPersistentCount = useCallback(
    (n: number, fonte: FontePersistida = "sino") => {
      persistidoRef.current = { ...persistidoRef.current, [fonte]: Math.max(0, n) };
      const { sino, chatInterno } = persistidoRef.current;
      setPersistido(sino + chatInterno);
      aplicar();
    },
    [aplicar],
  );

  /**
   * O `metadata` do App Router reescreve o <title> na hidratacao e a cada
   * navegacao, apagando o prefixo de pendencias. Reaplicar so no proximo evento
   * nao serve: com o contador PERSISTIDO, o usuario pode ficar minutos com a
   * aba mostrando "GK Platform" enquanto ha nao-lidas — e foi exatamente o que
   * apareceu no smoke, numa aba aberta direto em /dashboard.
   *
   * O observer torna isso deterministico, sem depender de timing nem de saber
   * quais rotas existem: se o titulo divergir do que este provider quer, ele
   * reescreve. A comparacao com `tituloDesejadoRef` evita loop — reescrever o
   * proprio valor nao dispara acao nova.
   */
  useEffect(() => {
    if (typeof document === "undefined") return;
    const titleEl = document.querySelector("title");
    if (!titleEl) return;

    const obs = new MutationObserver(() => {
      if (document.title !== tituloDesejadoRef.current) {
        document.title = tituloDesejadoRef.current;
      }
    });
    obs.observe(titleEl, { childList: true, characterData: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      // Zera SO o efemero: "aconteceu enquanto voce estava fora" foi consumido
      // pelo foco, mas nao-lida continua nao-lida ate o usuario abrir o sino.
      efemeroRef.current = 0;
      setEfemero(0);
      aplicar();
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [aplicar]);

  const value = useMemo(
    () => ({ notify, setPersistentCount, count: efemero + persistido }),
    [notify, setPersistentCount, efemero, persistido],
  );

  return (
    <TabBadgeContext.Provider value={value}>{children}</TabBadgeContext.Provider>
  );
}
